import { jsPDF } from 'jspdf';
import { barRects, totalModules } from './code128';

// Builds the product stickers as a downloadable PDF.
//
// Two output modes, and any physical size:
//   'a4'    — stickers tiled onto an A4 sheet with cut guides, for an ordinary
//             printer. The grid is worked out from the sticker size.
//   'label' — one sticker per page, sized exactly to the roll, for a thermal
//             label printer such as the Xprinter XP-365B.
//
// The layout is not a fixed set of coordinates. A 50×30mm thermal label cannot
// hold what a 2-inch square holds, so blocks are measured, and the optional
// ones are dropped in priority order until what is left fits. The barcode and
// the serial are never dropped — without them the sticker has no purpose.
//
// Barcodes are drawn as vector rectangles rather than rasterised images, which
// is what lets a code this small stay readable at 203 dpi.

const PT_PER_MM = 72 / 25.4;
const A4 = { width: 595.28, height: 841.89 };

export const mmToPt = (mm) => Number(mm || 0) * PT_PER_MM;
export const ptToMm = (pt) => Number(pt || 0) / PT_PER_MM;

// Common label stock. The 2-inch entries match what the system shipped with;
// the metric ones are the sizes Xprinter rolls are actually sold in.
export const STICKER_PRESETS = [
  { id: '50.8x50.8', label: '2 × 2 in  (50.8 × 50.8 mm)', widthMm: 50.8, heightMm: 50.8 },
  { id: '50.8x25.4', label: '2 × 1 in  (50.8 × 25.4 mm)', widthMm: 50.8, heightMm: 25.4 },
  { id: '40x30', label: '40 × 30 mm', widthMm: 40, heightMm: 30 },
  { id: '50x30', label: '50 × 30 mm', widthMm: 50, heightMm: 30 },
  { id: '50x40', label: '50 × 40 mm', widthMm: 50, heightMm: 40 },
  { id: '60x40', label: '60 × 40 mm', widthMm: 60, heightMm: 40 },
  { id: '70x50', label: '70 × 50 mm', widthMm: 70, heightMm: 50 },
  { id: '75x50', label: '75 × 50 mm', widthMm: 75, heightMm: 50 },
  { id: '80x50', label: '80 × 50 mm', widthMm: 80, heightMm: 50 },
];

export const presetIdFor = (widthMm, heightMm) => {
  const match = STICKER_PRESETS.find(
    (p) => Math.abs(p.widthMm - widthMm) < 0.6 && Math.abs(p.heightMm - heightMm) < 0.6
  );
  return match ? match.id : 'custom';
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// jsPDF's built-in fonts are Latin-1 only, so the Taka sign (U+09F3) cannot be
// drawn — it comes out as a wrong glyph or nothing. The currency label is
// therefore plain text the operator chooses ("Tk", "BDT", …).
const money = (n, label) => `${label || ''} ${Number(n || 0).toLocaleString('en-US')}`.trim();

// Strip anything the built-in fonts cannot draw, so a pasted character never
// turns into a wrong glyph on a printed label.
const printable = (value) =>
  String(value ?? '').replace(/[^\x20-\x7E -ÿ]/g, '').trim();

function formatStamp(value, mode) {
  const date = new Date(value || Date.now());
  if (mode === 'time') return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (mode === 'datetime') {
    return `${date.toLocaleDateString('en-GB')} ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  }
  return date.toLocaleDateString('en-GB');
}

// The barcode carries the serial without separators so the bars stay as wide as
// possible; the human-readable line below keeps the full serial.
const barcodePayload = (serial) => String(serial || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

// Work out what fits on a sticker of this size, and how big everything is.
// Returns the blocks to draw, top to bottom.
function planLayout(doc, { unit, product, options, width, height }) {
  const pad = clamp(Math.min(width, height) * 0.055, 2.5, 9);
  const inner = width - pad * 2;
  const scale = clamp(Number(options.textScale) || 1, 0.6, 1.6);

  const headerSize = clamp(height * 0.055 * scale, 4, 9);
  const nameSize = clamp(height * 0.072 * scale, 5, 11.5);
  const priceSize = clamp(height * 0.062 * scale, 4.5, 10);
  const metaSize = clamp(height * 0.05 * scale, 4, 8);
  const serialSize = clamp(height * 0.062 * scale, 4.5, 10);
  const footerSize = clamp(height * 0.045 * scale, 3.8, 7.5);
  const barcodeHeight = options.showBarcode
    ? clamp(height * (clamp(Number(options.barcodeHeightPct) || 30, 12, 50) / 100), 12, height * 0.55)
    : 0;

  // --- text content, after every show/hide switch ---
  const headerText = options.showHeader ? printable(options.headerText) : '';

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(nameSize);
  const maxNameLines = clamp(Math.round(Number(options.nameMaxLines) || 2), 1, 3);
  const nameLines = options.showProductName
    ? doc.splitTextToSize(printable(product?.name) || 'Product', inner).slice(0, maxNameLines)
    : [];

  const priceBits = [];
  if (options.showPrice) priceBits.push(money(product?.price, printable(options.currencyLabel)));
  if (options.showPoint) {
    priceBits.push(`${Number(product?.point || 0)} ${printable(options.pointLabel)}`.trim());
  }

  const metaBits = [];
  if (options.showBrand && product?.brand) metaBits.push(printable(product.brand));
  if (options.showCategory && product?.category) metaBits.push(printable(product.category));

  const footerBits = [];
  if (options.showBatch && unit.batchCode) footerBits.push(printable(unit.batchCode));
  if (options.showDate) footerBits.push(formatStamp(unit.createdAt, options.dateFormat));
  if (options.showFooter && options.footerText) footerBits.push(printable(options.footerText));

  // Every candidate block with the height it needs. Lower priority is dropped
  // first when the label is too small to hold everything — the barcode and the
  // serial (priority 99) are never dropped, since without them the sticker
  // cannot be scanned or read out.
  const blocks = [
    { id: 'header', h: headerSize * 1.5, priority: 1, skip: !headerText },
    { id: 'name1', h: nameSize * 1.15, priority: 4, skip: nameLines.length < 1 },
    { id: 'name2', h: nameSize * 1.15, priority: 2, skip: nameLines.length < 2 },
    { id: 'name3', h: nameSize * 1.15, priority: 1, skip: nameLines.length < 3 },
    { id: 'meta', h: metaSize * 1.3, priority: 0, skip: metaBits.length === 0 },
    { id: 'price', h: priceSize * 1.25, priority: 3, skip: priceBits.length === 0 },
    { id: 'barcode', h: barcodeHeight, priority: 99, skip: !options.showBarcode },
    { id: 'serial', h: serialSize * 1.5, priority: options.showBarcode ? 99 : 99, skip: !options.showSerialText },
    { id: 'footer', h: footerSize * 1.5, priority: 0, skip: footerBits.length === 0 },
  ].filter((b) => !b.skip);

  const available = height - pad * 2;
  const used = () => blocks.reduce((sum, b) => sum + b.h, 0);

  while (used() > available) {
    const droppable = blocks
      .filter((b) => b.priority < 99)
      .sort((a, b) => a.priority - b.priority)[0];
    if (!droppable) break;
    blocks.splice(blocks.indexOf(droppable), 1);
  }

  const has = (id) => blocks.some((b) => b.id === id);
  const shownNameLines = nameLines.slice(
    0,
    ['name1', 'name2', 'name3'].filter((id) => has(id)).length
  );

  // Spread any leftover room evenly between the blocks that survived.
  const slack = Math.max(available - used(), 0);
  const lead = Math.min(slack / Math.max(blocks.length - 1, 1), height * 0.06);

  return {
    pad,
    inner,
    headerSize,
    nameSize,
    priceSize,
    metaSize,
    serialSize,
    footerSize,
    barcodeHeight: has('barcode') ? barcodeHeight : 0,
    headerText: has('header') ? headerText : '',
    nameLines: shownNameLines,
    priceText: has('price') ? priceBits.join('   ·   ') : '',
    metaText: has('meta') ? metaBits.join('  ·  ') : '',
    serialText: has('serial') ? String(unit.serial) : '',
    footerText: has('footer') ? footerBits.join('  ·  ') : '',
    lead,
  };
}

// Turn a plan into a flat list of draw operations, positioned relative to the
// sticker's top-left corner.
//
// Both the PDF writer and the on-screen preview consume this same list, so the
// preview is the print — there is no second implementation of the layout to
// drift out of step.
function stickerOps(doc, { unit, product, options, width, height }) {
  const L = planLayout(doc, { unit, product, options, width, height });
  const centre = width / 2;
  const ops = [];
  let cursor = L.pad;

  const text = (value, size, weight, color, family = 'helvetica') => {
    ops.push({ kind: 'text', value, x: centre, y: cursor, size, weight, color, family });
  };

  if (L.headerText) {
    cursor += L.headerSize;
    text(L.headerText, L.headerSize, 'bold', '#475569');
    cursor += L.headerSize * 0.5;
  }

  for (const line of L.nameLines) {
    cursor += L.nameSize;
    text(line, L.nameSize, 'bold', '#111827');
    cursor += L.nameSize * 0.15;
  }

  if (L.metaText) {
    cursor += L.metaSize;
    text(L.metaText, L.metaSize, 'normal', '#94a3b8');
    cursor += L.metaSize * 0.3;
  }

  if (L.priceText) {
    cursor += L.lead * 0.4 + L.priceSize;
    text(L.priceText, L.priceSize, 'normal', '#475569');
    cursor += L.priceSize * 0.25;
  }

  if (L.barcodeHeight > 0) {
    cursor += L.lead;
    const payload = barcodePayload(unit.serial);
    const moduleWidth = L.inner / totalModules(payload);
    for (const bar of barRects(payload)) {
      ops.push({
        kind: 'rect',
        x: L.pad + bar.x * moduleWidth,
        y: cursor,
        w: bar.width * moduleWidth,
        h: L.barcodeHeight,
      });
    }
    cursor += L.barcodeHeight;
  }

  if (L.serialText) {
    cursor += L.serialSize * 1.15;
    text(L.serialText, L.serialSize, 'bold', '#111827', 'courier');
  }

  if (L.footerText) {
    cursor = height - L.pad * 0.6;
    text(L.footerText, L.footerSize, 'normal', '#6b7280');
  }

  return ops;
}

const hexToRgb = (hex) => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

// One sticker, top-left corner at (x, y), in a box of width × height points.
function drawSticker(doc, { unit, product, options }, x, y, width, height) {
  for (const op of stickerOps(doc, { unit, product, options, width, height })) {
    if (op.kind === 'rect') {
      doc.setFillColor(0, 0, 0);
      doc.rect(x + op.x, y + op.y, op.w, op.h, 'F');
      continue;
    }
    doc.setFont(op.family, op.weight);
    doc.setFontSize(op.size);
    doc.setTextColor(...hexToRgb(op.color));
    doc.text(op.value, x + op.x, y + op.y, { align: 'center' });
  }
}

// Faint cut guides so a full A4 sheet can be trimmed accurately.
function drawCutGuides(doc, x, y, width, height) {
  doc.setDrawColor(203, 213, 225);
  doc.setLineDashPattern([2, 2], 0);
  doc.setLineWidth(0.4);
  doc.rect(x, y, width, height);
  doc.setLineDashPattern([], 0);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

// Normalise a design from any source (server settings, or a per-print override
// in the dialog) into the exact shape the renderer expects.
export function resolveSticker(sticker) {
  const s = sticker || {};
  return {
    format: s.format === 'label' ? 'label' : 'a4',
    widthMm: clamp(Number(s.widthMm) || 50.8, 20, 210),
    heightMm: clamp(Number(s.heightMm) || 50.8, 15, 297),
    gapMm: clamp(Number(s.gapMm) || 0, 0, 20),
    pageMarginMm: clamp(Number(s.pageMarginMm ?? 8), 0, 30),
    showCutGuides: s.showCutGuides !== false,

    showHeader: Boolean(s.showHeader),
    headerText: s.headerText || '',

    showProductName: s.showProductName !== false,
    nameMaxLines: clamp(Math.round(Number(s.nameMaxLines) || 2), 1, 3),

    showPrice: s.showPrice !== false,
    currencyLabel: s.currencyLabel ?? 'Tk',
    showPoint: s.showPoint !== false,
    pointLabel: s.pointLabel ?? 'PV',
    showBrand: Boolean(s.showBrand),
    showCategory: Boolean(s.showCategory),

    showBarcode: s.showBarcode !== false,
    barcodeHeightPct: clamp(Number(s.barcodeHeightPct) || 30, 12, 50),
    showSerialText: s.showSerialText !== false,

    showBatch: s.showBatch !== false,
    showDate: s.showDate !== false,
    dateFormat: ['date', 'datetime', 'time'].includes(s.dateFormat) ? s.dateFormat : 'date',
    showFooter: s.showFooter !== false,
    footerText: s.footerText ?? '',

    textScale: clamp(Number(s.textScale) || 1, 0.6, 1.6),
  };
}

// How the chosen size tiles onto A4, and how readable the barcode will be.
// Used by the print dialog to warn before anything is wasted on paper.
export function sheetPlan(sticker, sampleSerial = 'CC-2026-000001') {
  const s = resolveSticker(sticker);
  const w = mmToPt(s.widthMm);
  const h = mmToPt(s.heightMm);
  const gap = mmToPt(s.gapMm);
  const margin = mmToPt(s.pageMarginMm);

  const columns = Math.max(Math.floor((A4.width - margin * 2 + gap) / (w + gap)), 0);
  const rows = Math.max(Math.floor((A4.height - margin * 2 + gap) / (h + gap)), 0);

  // The barcode spans the sticker minus its padding; a module narrower than
  // about 0.25mm gets unreliable on a 203 dpi thermal head (one dot = 0.125mm).
  const pad = clamp(Math.min(w, h) * 0.055, 2.5, 9);
  const barcodeWidth = w - pad * 2;
  const modules = totalModules(barcodePayload(sampleSerial));
  const moduleMm = ptToMm(barcodeWidth / modules);

  return {
    ...s,
    columns,
    rows,
    perPage: s.format === 'label' ? 1 : columns * rows,
    fitsOnA4: columns > 0 && rows > 0,
    moduleMm,
    barcodeOk: moduleMm >= 0.25,
    barcodeTight: moduleMm >= 0.19 && moduleMm < 0.25,
  };
}

export function buildStickerPdf({ units, product, sticker }) {
  const s = resolveSticker(sticker);
  // The design object is the options object — every switch reaches the
  // renderer, so there is no second place for a field to be forgotten.
  const options = s;
  const w = mmToPt(s.widthMm);
  const h = mmToPt(s.heightMm);

  if (s.format === 'label') {
    // One page per sticker, cut exactly to the roll.
    const doc = new jsPDF({ unit: 'pt', format: [w, h] });
    units.forEach((unit, index) => {
      if (index > 0) doc.addPage([w, h], w > h ? 'landscape' : 'portrait');
      drawSticker(doc, { unit, product, options }, 0, 0, w, h);
    });
    return doc;
  }

  const plan = sheetPlan(s);
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const gap = mmToPt(s.gapMm);
  const columns = Math.max(plan.columns, 1);
  const rows = Math.max(plan.rows, 1);
  const perPage = columns * rows;

  // Centre the grid on the page rather than hugging the margin — a centred
  // block is far easier to trim by hand.
  const gridWidth = columns * w + (columns - 1) * gap;
  const gridHeight = rows * h + (rows - 1) * gap;
  const originX = (A4.width - gridWidth) / 2;
  const originY = (A4.height - gridHeight) / 2;

  units.forEach((unit, index) => {
    const slot = index % perPage;
    if (index > 0 && slot === 0) doc.addPage('a4', 'portrait');
    const x = originX + (slot % columns) * (w + gap);
    const y = originY + Math.floor(slot / columns) * (h + gap);
    if (s.showCutGuides) drawCutGuides(doc, x, y, w, h);
    drawSticker(doc, { unit, product, options }, x, y, w, h);
  });

  return doc;
}

export function downloadStickerPdf({ units, product, sticker }) {
  const doc = buildStickerPdf({ units, product, sticker });
  const s = resolveSticker(sticker);
  const safeName = String(product?.name || 'product')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
    .slice(0, 40);
  const size = `${Math.round(s.widthMm)}x${Math.round(s.heightMm)}mm`;
  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`stickers-${safeName}-${units.length}-${size}-${stamp}.pdf`);
  return doc;
}

// On-screen preview of a single sticker at its true aspect ratio.
//
// It runs the same layout pass as the PDF — including jsPDF's own text
// measurement, so line wrapping matches exactly — and renders the resulting
// operations as SVG. What the operator sees is what the printer produces.
export function stickerPreviewSvg({ unit, product, sticker, maxPx = 210 }) {
  const s = resolveSticker(sticker);
  const w = mmToPt(s.widthMm);
  const h = mmToPt(s.heightMm);
  const scale = maxPx / Math.max(w, h);

  // A throwaway document, used only as a text-measuring device.
  const doc = new jsPDF({ unit: 'pt', format: [w, h] });
  const ops = stickerOps(doc, { unit, product, options: s, width: w, height: h });

  const FAMILY = { helvetica: 'Helvetica, Arial, sans-serif', courier: 'Courier, monospace' };
  const body = ops
    .map((op) => {
      if (op.kind === 'rect') {
        return `<rect x="${(op.x * scale).toFixed(2)}" y="${(op.y * scale).toFixed(2)}" width="${(op.w * scale).toFixed(2)}" height="${(op.h * scale).toFixed(2)}" fill="#000"/>`;
      }
      return `<text x="${(op.x * scale).toFixed(2)}" y="${(op.y * scale).toFixed(2)}" text-anchor="middle" font-family="${FAMILY[op.family]}" font-weight="${op.weight}" font-size="${(op.size * scale).toFixed(2)}" fill="${op.color}">${escapeXml(op.value)}</text>`;
    })
    .join('');

  const width = w * scale;
  const height = h * scale;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width.toFixed(2)} ${height.toFixed(2)}" width="${width.toFixed(0)}" height="${height.toFixed(0)}">
    <rect width="${width.toFixed(2)}" height="${height.toFixed(2)}" fill="#fff" stroke="#cbd5e1"${s.showCutGuides ? ' stroke-dasharray="3 3"' : ''}/>
    ${body}
  </svg>`;
}

const escapeXml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
